import React from 'react'
import { Box, Checkbox, TextField, Typography } from '@mui/material'

import { Button } from '../../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../../constants/colors'
import type { UseUnseeAchievementsResult } from '../hooks/useUnseeAchievements'

interface UnseeAchievementsToolProps {
  hook: UseUnseeAchievementsResult
}

/**
 * Dev tool — search users, single-select one, then call the backend "unsee all
 * achievements" endpoint for that user so they re-experience the unlock toasts.
 */
export const UnseeAchievementsTool: React.FC<UnseeAchievementsToolProps> = ({ hook }) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: INTERAX_COLORS.white,
          marginBottom: '8px'
        }}
      >
        Unsee achievements by user
      </Typography>

      <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          value={hook.usersQuery}
          onChange={(e) => hook.setUsersQuery(e.target.value)}
          placeholder="Search user email, name, or id"
          sx={{
            minWidth: { xs: '100%', sm: 360 },
            '& .MuiInputBase-root': { color: INTERAX_COLORS.white },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: INTERAX_COLORS.thunderGrey20 }
          }}
        />

        <Button variant="secondary" onClick={hook.loadUsers}>
          {hook.usersLoading ? 'Loading…' : 'Search'}
        </Button>

        <Button
          variant="primary"
          disabled={hook.achievementsLoading || !hook.selectedUserId}
          onClick={() => hook.runUnsee(hook.selectedUserId)}
        >
          {hook.achievementsLoading ? 'Unseeing…' : 'Unsee for selected'}
        </Button>
      </Box>

      {hook.users.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {hook.users.slice(0, 30).map((u) => {
            const selected = u.id === hook.selectedUserId
            return (
              <Box
                key={u.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '6px 8px',
                  border: `1px solid ${INTERAX_COLORS.thunderGrey20}`,
                  borderRadius: '6px'
                }}
              >
                <Checkbox
                  checked={selected}
                  onChange={() => hook.setSelectedUserId(selected ? '' : u.id)}
                  sx={{
                    color: INTERAX_COLORS.thunderGrey,
                    '&.Mui-checked': { color: INTERAX_COLORS.sun }
                  }}
                />
                <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.white }}>{u.email}</Typography>
                <Typography sx={{ fontSize: '12px', color: INTERAX_COLORS.thunderGrey }}>
                  {u.name} · {u.id}
                </Typography>
              </Box>
            )
          })}
          {hook.users.length > 30 && (
            <Typography sx={{ fontSize: '12px', color: INTERAX_COLORS.thunderGrey }}>
              Showing first 30 results. Narrow your search for more.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}
